import { ContactSchema, DealSchema, RoomSchema } from '../definitions/schemas';
import { escapedCompanyWildcard } from '../enums/RoomTemplateWildcard';
import { getDisplayName } from './DisplayName';
import { RoomStatus } from '../enums/RoomStatus';
import UserStore from '../stores/observables/UserStore';

/**
 * Content description and name follow levels of precedence
 *   1. If there is any non-undefined value in the structure, it should display
 *      as-is including empty spaces
 *   2. If there is any available value from the original content definition
 *   3. Fallback to empty string
 * This happens across several locations (review step, room and others)
 */
export const getRoomContentText = (
  structureContentValue: string | undefined,
  contentValue: string | undefined
) =>
  typeof structureContentValue === 'string'
    ? structureContentValue
    : contentValue || '';

/**
 * Determines the target for a room, following these rules:
 *  1. If the room's deal has a company, use the company's name
 *  2. If there's no deal/deal company, use the primary contact's name
 */
export const getRoomTarget = (
  deal?: DealSchema,
  primaryContact?: ContactSchema
): string => {
  let target = '';
  if (deal?.company?.name) {
    target = deal.company.name;
  } else if (primaryContact?.company) {
    target = primaryContact.company.name;
  } else if (primaryContact) {
    target = getDisplayName(primaryContact);
  }

  return target;
};

export const MAX_ROOM_NAME_LENGTH = 255;

export const getSuggestedRoomName = (
  deal?: DealSchema,
  primaryContact?: ContactSchema
): string => {
  const roomTarget = getRoomTarget(deal, primaryContact);
  return roomTarget !== ''
    ? `Room prepared for ${roomTarget}`.slice(0, MAX_ROOM_NAME_LENGTH)
    : '';
};

export const isRoomDisabled = (room: RoomSchema): boolean =>
  room.status === RoomStatus.INACTIVE;

export const isDraftRoom = (room: RoomSchema): boolean =>
  room.status === RoomStatus.DRAFT;

export const isRoomPublic = (room: RoomSchema) =>
  !(room.requiresEmail || room.requiresPasscode);

/**
 * Replaces the company wildcard from a name within the room by the room's target,
 * whether that would be the deal company's name or the primary contact's
 * name or email.
 */
export const getProcessedName = (
  name: string,
  deal: DealSchema | undefined,
  primaryContact: ContactSchema | undefined
): string => {
  const wildcardRegExp = new RegExp(escapedCompanyWildcard, 'g');
  if (wildcardRegExp.test(name)) {
    const target = getRoomTarget(deal, primaryContact);
    // only replace the wildcard if the target is not empty
    if (target.trim().length > 0) {
      return name.replace(new RegExp(escapedCompanyWildcard, 'g'), target);
    }
  }

  return name;
};

/**
 * Checks if a room has activity since its owner or a collaborator last accessed the room's
 * overview page, based on the timestamp given by the `lastActivity`
 * argument. This is only true if the current logged user is the
 * room's owner.
 */
export const hasActivitySinceOwnerLastAccess = (
  room: RoomSchema,
  lastActivityTimestamp?: number
) => {
  const currentUser = UserStore.get();
  const collaborator = room.roomCollaborators?.find(
    (collaborator) => collaborator.collaboratorId === currentUser?.id
  );

  if (!collaborator || !lastActivityTimestamp) {
    return false;
  }

  const { lastAccessAt } = collaborator;

  return !lastAccessAt || lastAccessAt < lastActivityTimestamp;
};
